import React, { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import { LangContext } from '../i18n/LangContext';
import { AuthContext } from '../contexts/AuthContext';
import { UPLOADS_URL, BACKEND_URL } from '../config';

const ProjectsList = () => {
  const [projects, setProjects] = useState([]);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [sortBy, setSortBy] = useState('recent');
  const [viewMode, setViewMode] = useState('grid');
  const [loading, setLoading] = useState(true);
  useContext(LangContext);
  useContext(AuthContext);

  useEffect(() => {
    loadProjects();
  }, []);

  const loadProjects = async () => {
    try {
      const res = await api.get('/projects/accepted');
      const projectsWithImages = await Promise.all(
        (res.data || []).map(async (project) => {
          try {
            const imagesRes = await api.get(`/projects/${project.id}/images`);
            return { ...project, images: imagesRes.data || [] };
          } catch {
            return { ...project, images: [] };
          }
        })
      );
      setProjects(projectsWithImages);
    } catch (err) {
      console.error(err);
      setProjects([]);
    } finally {
      setLoading(false);
    }
  };

  const getCategoryInfo = (cat) => {
    const categories = {
      commerce: { icon: '🛒', label: 'Commerce', color: 'from-orange-500 to-amber-500', bg: 'bg-orange-100 text-orange-700' },
      tech: { icon: '💻', label: 'Tech / Startup', color: 'from-blue-500 to-cyan-500', bg: 'bg-blue-100 text-blue-700' },
      services: { icon: '🔧', label: 'Services', color: 'from-gray-500 to-slate-500', bg: 'bg-gray-100 text-gray-700' },
      agriculture: { icon: '🌾', label: 'Agriculture', color: 'from-green-500 to-emerald-500', bg: 'bg-green-100 text-green-700' },
      immobilier: { icon: '🏠', label: 'Immobilier', color: 'from-yellow-600 to-orange-500', bg: 'bg-yellow-100 text-yellow-700' },
      transport: { icon: '🚚', label: 'Transport', color: 'from-indigo-500 to-blue-500', bg: 'bg-indigo-100 text-indigo-700' },
      restauration: { icon: '🍽️', label: 'Restauration', color: 'from-red-500 to-pink-500', bg: 'bg-red-100 text-red-700' },
      mode: { icon: '👗', label: 'Mode', color: 'from-pink-500 to-rose-500', bg: 'bg-pink-100 text-pink-700' },
      sante: { icon: '💊', label: 'Santé', color: 'from-teal-500 to-cyan-500', bg: 'bg-teal-100 text-teal-700' },
      education: { icon: '📚', label: 'Éducation', color: 'from-purple-500 to-violet-500', bg: 'bg-purple-100 text-purple-700' },
      finance: { icon: '💰', label: 'Finance', color: 'from-emerald-500 to-green-500', bg: 'bg-emerald-100 text-emerald-700' },
      autre: { icon: '📁', label: 'Autre', color: 'from-gray-500 to-gray-600', bg: 'bg-gray-100 text-gray-700' }
    };
    return categories[cat] || categories.autre;
  };

  const getTaskTypeInfo = (type) => {
    const types = {
      collaboration: { icon: '🤝', label: 'Associé', bg: 'bg-purple-100 text-purple-700' },
      prestation: { icon: '💰', label: 'Freelance', bg: 'bg-green-100 text-green-700' },
      emploi: { icon: '👔', label: 'Emploi', bg: 'bg-blue-100 text-blue-700' },
      stage: { icon: '🎓', label: 'Stage', bg: 'bg-orange-100 text-orange-700' },
      partenariat: { icon: '🤝', label: 'Partenaire', bg: 'bg-indigo-100 text-indigo-700' }
    };
    return types[type] || { icon: '📋', label: type || 'Rôle', bg: 'bg-gray-100 text-gray-700' };
  };

  const getStatusInfo = (status) => {
    const statuses = {
      actif: { icon: '🟢', label: 'Actif', bg: 'bg-green-500', text: 'text-white' },
      en_cours: { icon: '🔵', label: 'En cours', bg: 'bg-blue-500', text: 'text-white' },
      termine: { icon: '✅', label: 'Terminé', bg: 'bg-gray-500', text: 'text-white' },
      annule: { icon: '❌', label: 'Annulé', bg: 'bg-red-500', text: 'text-white' }
    };
    return statuses[status] || statuses.actif;
  };

  // Get unique categories from projects
  const availableCategories = [...new Set(projects.map(p => p.categorie).filter(Boolean))];

  // Filter and sort projects
  const filteredProjects = projects
    .filter(p => {
      const matchesSearch = p.titre?.toLowerCase().includes(search.toLowerCase()) ||
                           p.description?.toLowerCase().includes(search.toLowerCase()) ||
                           p.author_nom?.toLowerCase().includes(search.toLowerCase());
      const matchesStatus = filter === 'all' || p.statut === filter;
      const matchesCategory = categoryFilter === 'all' || p.categorie === categoryFilter;
      return matchesSearch && matchesStatus && matchesCategory;
    })
    .sort((a, b) => {
      if (sortBy === 'recent') return new Date(b.date_creation) - new Date(a.date_creation);
      if (sortBy === 'oldest') return new Date(a.date_creation) - new Date(b.date_creation);
      if (sortBy === 'alpha') return a.titre.localeCompare(b.titre);
      return 0;
    });

  // Stats
  const stats = {
    total: projects.length,
    actifs: projects.filter(p => p.statut === 'actif' || p.statut === 'en_cours').length,
    termines: projects.filter(p => p.statut === 'termine').length
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="relative w-20 h-20 mx-auto mb-6">
            <div className="absolute inset-0 rounded-full border-4 border-purple-500/30"></div>
            <div className="absolute inset-0 rounded-full border-4 border-transparent border-t-purple-500 animate-spin"></div>
            <div className="absolute inset-2 rounded-full border-4 border-transparent border-t-pink-500 animate-spin" style={{ animationDirection: 'reverse', animationDuration: '1.5s' }}></div>
          </div>
          <p className="text-white/70 text-lg">Chargement de vos projets...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-14 h-14 bg-gradient-to-br from-purple-500 to-pink-500 rounded-2xl flex items-center justify-center shadow-lg shadow-purple-500/30">
                  <span className="text-3xl">🚀</span>
                </div>
                <div>
                  <h1 className="text-3xl md:text-4xl font-bold">Projets associés</h1>
                  <p className="text-purple-200">Projets où vous êtes collaborateur</p>
                </div>
              </div>
              
              {/* Stats Pills */}
              <div className="flex flex-wrap gap-3 mt-6">
                <div className="px-4 py-2 bg-white/10 backdrop-blur rounded-full flex items-center gap-2">
                  <span className="text-2xl">📊</span>
                  <span className="font-semibold">{stats.total}</span>
                  <span className="text-purple-200">projet{stats.total > 1 ? 's' : ''}</span>
                </div>
                <div className="px-4 py-2 bg-green-500/20 backdrop-blur rounded-full flex items-center gap-2">
                  <span className="text-2xl">🟢</span>
                  <span className="font-semibold">{stats.actifs}</span>
                  <span className="text-green-200">actif{stats.actifs > 1 ? 's' : ''}</span>
                </div>
                <div className="px-4 py-2 bg-blue-500/20 backdrop-blur rounded-full flex items-center gap-2">
                  <span className="text-2xl">✅</span>
                  <span className="font-semibold">{stats.termines}</span>
                  <span className="text-blue-200">terminé{stats.termines > 1 ? 's' : ''}</span>
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="flex flex-col sm:flex-row gap-3">
              <Link
                to="/"
                className="px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white font-semibold rounded-xl hover:from-purple-600 hover:to-pink-600 transition shadow-lg shadow-purple-500/30 flex items-center justify-center gap-2"
              >
                <span>🎯</span>
                Trouver des opportunités
              </Link>
              <Link
                to="/my-applications"
                className="px-6 py-3 bg-white/10 backdrop-blur text-white font-semibold rounded-xl hover:bg-white/20 transition flex items-center justify-center gap-2"
              >
                <span>📝</span>
                Mes candidatures
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {projects.length > 0 ? (
          <>
            {/* Search & Filters Bar */}
            <div className="bg-white rounded-2xl shadow-lg p-4 mb-8 -mt-8 relative z-10">
              <div className="flex flex-col lg:flex-row gap-4">
                {/* Search */}
                <div className="flex-1 relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">🔍</span>
                  <input
                    type="text"
                    placeholder="Rechercher un projet, auteur..."
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition"
                  />
                </div>

                {/* Filters */}
                <div className="flex flex-wrap gap-2 items-center">
                  {/* Status Filter */}
                  <select
                    value={filter}
                    onChange={e => setFilter(e.target.value)}
                    className="px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 bg-white"
                  >
                    <option value="all">Tous les statuts</option>
                    <option value="actif">🟢 Actif</option>
                    <option value="en_cours">🔵 En cours</option>
                    <option value="termine">✅ Terminé</option>
                  </select>

                  {/* Category Filter */}
                  {availableCategories.length > 0 && (
                    <select
                      value={categoryFilter}
                      onChange={e => setCategoryFilter(e.target.value)}
                      className="px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 bg-white"
                    >
                      <option value="all">Toutes catégories</option>
                      {availableCategories.map(cat => {
                        const info = getCategoryInfo(cat);
                        return (
                          <option key={cat} value={cat}>{info.icon} {info.label}</option>
                        );
                      })}
                    </select>
                  )}

                  {/* Sort */}
                  <select
                    value={sortBy}
                    onChange={e => setSortBy(e.target.value)}
                    className="px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 bg-white"
                  >
                    <option value="recent">Plus récents</option>
                    <option value="oldest">Plus anciens</option>
                    <option value="alpha">A-Z</option>
                  </select>

                  {/* View Mode Toggle */}
                  <div className="flex bg-gray-100 rounded-xl p-1">
                    <button
                      onClick={() => setViewMode('grid')}
                      className={`p-2 rounded-lg transition ${viewMode === 'grid' ? 'bg-white shadow text-purple-600' : 'text-gray-500'}`}
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                      </svg>
                    </button>
                    <button
                      onClick={() => setViewMode('list')}
                      className={`p-2 rounded-lg transition ${viewMode === 'list' ? 'bg-white shadow text-purple-600' : 'text-gray-500'}`}
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>

              {/* Active Filters */}
              {(search || filter !== 'all' || categoryFilter !== 'all') && (
                <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t">
                  <span className="text-sm text-gray-500">Filtres actifs:</span>
                  {search && (
                    <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm flex items-center gap-1">
                      "{search}"
                      <button onClick={() => setSearch('')} className="ml-1 hover:text-purple-900">×</button>
                    </span>
                  )}
                  {filter !== 'all' && (
                    <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm flex items-center gap-1">
                      {getStatusInfo(filter).label}
                      <button onClick={() => setFilter('all')} className="ml-1 hover:text-blue-900">×</button>
                    </span>
                  )}
                  {categoryFilter !== 'all' && (
                    <span className="px-3 py-1 bg-orange-100 text-orange-700 rounded-full text-sm flex items-center gap-1">
                      {getCategoryInfo(categoryFilter).label}
                      <button onClick={() => setCategoryFilter('all')} className="ml-1 hover:text-orange-900">×</button>
                    </span>
                  )}
                  <button
                    onClick={() => { setSearch(''); setFilter('all'); setCategoryFilter('all'); }}
                    className="text-sm text-gray-500 hover:text-gray-700 underline"
                  >
                    Effacer tout
                  </button>
                </div>
              )}
            </div>

            {/* Results Count */}
            <div className="flex items-center justify-between mb-6">
              <p className="text-gray-600">
                <span className="font-semibold text-gray-900">{filteredProjects.length}</span> projet{filteredProjects.length > 1 ? 's' : ''} trouvé{filteredProjects.length > 1 ? 's' : ''}
              </p>
            </div>

            {/* Projects Grid/List */}
            {filteredProjects.length > 0 ? (
              viewMode === 'grid' ? (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {filteredProjects.map((project, index) => {
                    const categoryInfo = getCategoryInfo(project.categorie);
                    const statusInfo = getStatusInfo(project.statut);
                    const taskInfo = getTaskTypeInfo(project.accepted_task_type);
                    
                    return (
                      <Link
                        key={project.id}
                        to={`/project/${project.id}`}
                        className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 group"
                        style={{ animationDelay: `${index * 50}ms` }}
                      >
                        {/* Image */}
                        <div className="h-48 relative overflow-hidden">
                          {project.image_couverture ? (
                            <img
                              src={`${UPLOADS_URL}projects/${project.image_couverture}`}
                              alt={project.titre}
                              className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling && (e.target.nextSibling.style.display = 'flex');
                              }}
                            />
                          ) : project.images && project.images.length > 0 ? (
                            <img
                              src={`${UPLOADS_URL}${project.images[0].image_path}`}
                              alt={project.titre}
                              className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                              onError={(e) => {
                                e.target.style.display = 'none';
                              }}
                            />
                          ) : (
                            <div className={`w-full h-full bg-gradient-to-br ${categoryInfo.color} flex items-center justify-center`}>
                              <span className="text-6xl opacity-50">{categoryInfo.icon}</span>
                            </div>
                          )}
                          
                          {/* Overlay Gradient */}
                          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                          
                          {/* Status Badge */}
                          <div className="absolute top-3 right-3">
                            <span className={`px-3 py-1.5 ${statusInfo.bg} ${statusInfo.text} rounded-full text-xs font-bold shadow-lg flex items-center gap-1`}>
                              {statusInfo.icon} {statusInfo.label}
                            </span>
                          </div>

                          {/* Category Badge */}
                          {project.categorie && (
                            <div className="absolute top-3 left-3">
                              <span className={`px-3 py-1.5 ${categoryInfo.bg} rounded-full text-xs font-semibold shadow`}>
                                {categoryInfo.icon} {categoryInfo.label}
                              </span>
                            </div>
                          )}

                          {/* Role Badge - Bottom */}
                          {project.accepted_task_titre && (
                            <div className="absolute bottom-3 left-3 right-3">
                              <span className="px-3 py-2 bg-white/95 backdrop-blur rounded-xl text-sm font-semibold text-gray-800 shadow-lg flex items-center gap-2">
                                <span className={`px-2 py-0.5 ${taskInfo.bg} rounded-full text-xs`}>
                                  {taskInfo.icon} {taskInfo.label}
                                </span>
                                <span className="truncate">{project.accepted_task_titre}</span>
                              </span>
                            </div>
                          )}
                        </div>

                        {/* Content */}
                        <div className="p-5">
                          <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-purple-600 transition line-clamp-1">
                            {project.titre}
                          </h3>
                          <p className="text-gray-500 text-sm mb-4 line-clamp-2">
                            {project.description_courte || project.description || 'Aucune description'}
                          </p>

                          {/* Footer */}
                          <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                            <div className="flex items-center gap-2">
                              {project.photo_profil ? (
                                <img
                                  src={`${BACKEND_URL}/${project.photo_profil}`}
                                  alt={project.author_nom}
                                  className="w-8 h-8 rounded-full object-cover ring-2 ring-purple-100"
                                />
                              ) : (
                                <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center">
                                  <span className="text-white font-semibold text-sm">
                                    {project.author_nom?.[0] || 'A'}
                                  </span>
                                </div>
                              )}
                              <span className="text-sm text-gray-600 font-medium">{project.author_nom}</span>
                            </div>
                            <span className="text-purple-600 text-sm font-semibold group-hover:translate-x-1 transition-transform flex items-center gap-1">
                              Ouvrir
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                              </svg>
                            </span>
                          </div>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              ) : (
                /* List View */
                <div className="space-y-4">
                  {filteredProjects.map((project) => {
                    const categoryInfo = getCategoryInfo(project.categorie);
                    const statusInfo = getStatusInfo(project.statut);
                    const taskInfo = getTaskTypeInfo(project.accepted_task_type);
                    
                    return (
                      <Link
                        key={project.id}
                        to={`/project/${project.id}`}
                        className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition flex flex-col md:flex-row group"
                      >
                        {/* Image */}
                        <div className="md:w-64 h-48 md:h-auto relative flex-shrink-0 overflow-hidden">
                          {project.image_couverture ? (
                            <img
                              src={`${UPLOADS_URL}projects/${project.image_couverture}`}
                              alt={project.titre}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                              onError={(e) => {
                                e.target.style.display = 'none';
                              }}
                            />
                          ) : project.images && project.images.length > 0 ? (
                            <img
                              src={`${UPLOADS_URL}${project.images[0].image_path}`}
                              alt={project.titre}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                            />
                          ) : (
                            <div className={`w-full h-full bg-gradient-to-br ${categoryInfo.color} flex items-center justify-center`}>
                              <span className="text-5xl opacity-50">{categoryInfo.icon}</span>
                            </div>
                          )}
                        </div>

                        {/* Content */}
                        <div className="flex-1 p-6 flex flex-col justify-between">
                          <div>
                            <div className="flex flex-wrap items-center gap-2 mb-3">
                              <span className={`px-3 py-1 ${statusInfo.bg} ${statusInfo.text} rounded-full text-xs font-bold`}>
                                {statusInfo.icon} {statusInfo.label}
                              </span>
                              {project.categorie && (
                                <span className={`px-3 py-1 ${categoryInfo.bg} rounded-full text-xs font-semibold`}>
                                  {categoryInfo.icon} {categoryInfo.label}
                                </span>
                              )}
                              {project.accepted_task_titre && (
                                <span className={`px-3 py-1 ${taskInfo.bg} rounded-full text-xs font-semibold`}>
                                  {taskInfo.icon} {project.accepted_task_titre}
                                </span>
                              )}
                            </div>

                            <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-purple-600 transition">
                              {project.titre}
                            </h3>
                            <p className="text-gray-500 line-clamp-2">
                              {project.description_courte || project.description || 'Aucune description'}
                            </p>
                          </div>

                          <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-100">
                            <div className="flex items-center gap-3">
                              {project.photo_profil ? (
                                <img
                                  src={`${BACKEND_URL}/${project.photo_profil}`}
                                  alt={project.author_nom}
                                  className="w-10 h-10 rounded-full object-cover ring-2 ring-purple-100"
                                />
                              ) : (
                                <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center">
                                  <span className="text-white font-semibold">
                                    {project.author_nom?.[0] || 'A'}
                                  </span>
                                </div>
                              )}
                              <div>
                                <p className="font-medium text-gray-900">{project.author_nom}</p>
                                <p className="text-sm text-gray-500">Auteur du projet</p>
                              </div>
                            </div>
                            <span className="px-4 py-2 bg-purple-100 text-purple-700 rounded-xl font-semibold group-hover:bg-purple-600 group-hover:text-white transition">
                              Voir le projet →
                            </span>
                          </div>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              )
            ) : (
              /* No Results */
              <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
                <div className="text-6xl mb-4">🔍</div>
                <h3 className="text-xl font-semibold text-gray-700 mb-2">Aucun résultat</h3>
                <p className="text-gray-500 mb-6">Aucun projet ne correspond à vos critères de recherche.</p>
                <button
                  onClick={() => { setSearch(''); setFilter('all'); setCategoryFilter('all'); }}
                  className="px-6 py-3 bg-purple-600 text-white rounded-xl font-semibold hover:bg-purple-700 transition"
                >
                  Réinitialiser les filtres
                </button>
              </div>
            )}
          </>
        ) : (
          /* Empty State - No Projects */
          <div className="bg-white rounded-2xl shadow-lg p-12 text-center -mt-8 relative z-10">
            <div className="max-w-md mx-auto">
              <div className="w-24 h-24 bg-gradient-to-br from-purple-100 to-pink-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-5xl">🎯</span>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">
                Aucun projet pour l'instant
              </h3>
              <p className="text-gray-500 mb-8">
                Vous n'avez pas encore été accepté dans un projet. Parcourez les opportunités et postulez pour rejoindre des équipes passionnantes !
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/"
                  className="px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white font-semibold rounded-xl hover:from-purple-600 hover:to-pink-600 transition shadow-lg shadow-purple-500/30"
                >
                  🔍 Parcourir les opportunités
                </Link>
                <Link
                  to="/my-applications"
                  className="px-6 py-3 bg-gray-100 text-gray-700 font-semibold rounded-xl hover:bg-gray-200 transition"
                >
                  📝 Voir mes candidatures
                </Link>
              </div>
            </div>
          </div>
        )}

        {/* Quick Links Section */}
        {projects.length > 0 && (
          <div className="mt-12 grid md:grid-cols-3 gap-6">
            <Link
              to="/my-applications"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition group"
            >
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-2xl flex items-center justify-center shadow-lg shadow-orange-500/30 group-hover:scale-110 transition">
                  <span className="text-2xl">📝</span>
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 group-hover:text-orange-600 transition">Mes candidatures</h4>
                  <p className="text-sm text-gray-500">Suivre vos candidatures en cours</p>
                </div>
              </div>
            </Link>
            
            <Link
              to="/"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition group"
            >
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-gradient-to-br from-purple-500 to-pink-500 rounded-2xl flex items-center justify-center shadow-lg shadow-purple-500/30 group-hover:scale-110 transition">
                  <span className="text-2xl">🎯</span>
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 group-hover:text-purple-600 transition">Pour Toi</h4>
                  <p className="text-sm text-gray-500">Découvrir de nouvelles opportunités</p>
                </div>
              </div>
            </Link>

            <Link
              to="/profile"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition group"
            >
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-2xl flex items-center justify-center shadow-lg shadow-blue-500/30 group-hover:scale-110 transition">
                  <span className="text-2xl">👤</span>
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 group-hover:text-blue-600 transition">Mon profil</h4>
                  <p className="text-sm text-gray-500">Gérer vos informations</p>
                </div>
              </div>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProjectsList;
